import React from 'react';
import { Tree, Space, Button, Tooltip, Badge, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import { MuscleGroup } from '../../types';

const { Text } = Typography;

interface MuscleGroupTreeProps {
  muscleGroups: MuscleGroup[];
  selectedKeys: React.Key[];
  expandedKeys: React.Key[];
  onSelect: (keys: React.Key[]) => void;
  onExpand: (keys: React.Key[]) => void;
  onEdit: (muscleGroup: MuscleGroup) => void;
  onDelete: (muscleGroup: MuscleGroup) => void;
}

const MuscleGroupTree: React.FC<MuscleGroupTreeProps> = ({
  muscleGroups,
  selectedKeys,
  expandedKeys,
  onSelect,
  onExpand,
  onEdit,
  onDelete,
}) => {
  const getTreeData = (): DataNode[] => {
    const categories: { [key: string]: any } = {};
    
    muscleGroups.forEach(mg => {
      const category = mg.category || 'Uncategorized';
      
      if (!categories[category]) {
        categories[category] = {
          title: (
            <Space>
              <Text strong>{category}</Text>
              <Badge count={0} />
            </Space>
          ),
          key: category,
          children: [],
        };
      }
      
      if (mg.subCategory) {
        let subCategory = categories[category].children.find((sub: any) => sub.key === `${category}-${mg.subCategory}`);
        if (!subCategory) {
          subCategory = {
            title: (
              <Space>
                <Text>{mg.subCategory}</Text>
                <Badge count={0} size="small" />
              </Space>
            ),
            key: `${category}-${mg.subCategory}`,
            children: [],
          };
          categories[category].children.push(subCategory);
        }
        
        subCategory.children.push(createMuscleGroupNode(mg));
      } else {
        categories[category].children.push(createMuscleGroupNode(mg));
      }
    });
    
    return Object.values(categories);
  };

  const createMuscleGroupNode = (mg: MuscleGroup): DataNode => ({
    title: (
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <Text>{mg.name}</Text>
          {mg.split && <Badge count="L/R" size="small" style={{ backgroundColor: '#87d068' }} />}
        </Space>
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(mg);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={(e) => {
                e.stopPropagation();
                onDelete(mg);
              }}
            />
          </Tooltip>
        </Space>
      </Space>
    ),
    key: mg.id,
    isLeaf: true,
  });

  return (
    <Tree
      treeData={getTreeData()}
      selectedKeys={selectedKeys}
      expandedKeys={expandedKeys}
      onSelect={onSelect}
      onExpand={onExpand}
      showLine={{ showLeafIcon: false }}
      defaultExpandAll
    />
  );
};

export default MuscleGroupTree; 